import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  Cog6ToothIcon,
  FolderIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useState, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';

import {
  formatBadge,
  navigationFor,
  type NavBadge,
  type NavItem,
  type Navigation,
} from '../../lib/navigation';
import type { ProjectListItem } from '../../lib/types';
import { BRAND_LOGO, BRAND_MARK } from '../GreetingOverlay';

/** Koľko projektov je vidieť bez rozbalenia. */
export const SIDEBAR_PROJECTS_VISIBLE = 6;

export type SidebarProject = Pick<ProjectListItem, 'id' | 'name' | 'color' | 'unreadMessages'> & {
  workers: ReadonlyArray<{ userId: number }>;
};

type SidebarProps = {
  rail: boolean;
  onToggleRail: () => void;
  counts: Record<NavBadge, number>;
  hasAppUpdate: boolean;
  /** Už zoradené (živé a s neprečítanými hore). */
  projects: ReadonlyArray<SidebarProject>;
  myUserId?: number;
  canCreateProjects: boolean;
  onOpenSearch: () => void;
  onOpenJarvis: () => void;
  jarvisOpen: boolean;
  /** Profil s menu — rám appky ho skladá sám (lenivé Headless UI). */
  profile: ReactNode;
  /** Zvonček aktivity vedľa loga (potrebuje prihláseného človeka, preto zvonka). */
  bell?: ReactNode;
  /** Čo sa v paneli ukáže — hosť má užší výber (lib/navigation.ts). */
  navigation?: Navigation;
};

const itemClass = ({ isActive }: { isActive: boolean }) =>
  clsx('sidebar-item', isActive && 'sidebar-item-active');

const NavRow = ({ item, count, rail }: { item: NavItem; count: number; rail: boolean }) => {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={itemClass}
      // V úzkom režime nie je vidieť text, takže meno nesie bublina.
      title={rail ? (count > 0 ? `${item.label} (${count})` : item.label) : undefined}
    >
      <Icon className="sidebar-icon" aria-hidden="true" />
      <span className="sidebar-label truncate">{item.label}</span>
      {count > 0 ? (
        <span className="sidebar-badge" aria-label={`${count} neprečítaných`}>
          {formatBadge(count)}
        </span>
      ) : null}
    </NavLink>
  );
};

/**
 * Bočný panel (desktop). Tri skupiny — Dnes, Tím, Projekty — hore hľadanie,
 * dole JARVIS, nastavenia a vlastný profil so stavom. Dá sa zúžiť na ikony
 * (tlačidlo alebo „[“); voľbu si pamätá useSidebarRail.
 *
 * Zámerne bez backdrop-filter aj bez nekonečných animácií: panel je na
 * obrazovke celý deň a veľký. Kým mal „vibrancy“ rozmazanie a pulzujúce
 * bodky pri živých projektoch, prekresľoval sa aj vtedy, keď sa nič nedialo.
 */
export const Sidebar = ({
  rail,
  onToggleRail,
  counts,
  hasAppUpdate,
  projects,
  myUserId,
  canCreateProjects,
  onOpenSearch,
  onOpenJarvis,
  jarvisOpen,
  profile,
  bell,
  navigation = navigationFor(undefined),
}: SidebarProps) => {
  const [allProjectsShown, setAllProjectsShown] = useState(false);
  const shown = allProjectsShown || rail ? projects : projects.slice(0, SIDEBAR_PROJECTS_VISIBLE);
  const countFor = (item: NavItem) => (item.badge ? counts[item.badge] : 0);

  return (
    <aside
      className={clsx('app-sidebar hidden md:flex md:h-screen md:flex-col md:sticky md:top-0', rail && 'sidebar-rail')}
      aria-label="Hlavná navigácia"
      data-rail={rail ? 'true' : 'false'}
    >
      <div className={clsx('flex items-center gap-1 pb-2 pt-3.5', rail ? 'flex-col px-2' : 'px-4')}>
        <Link to="/" className="flex min-h-[32px] items-center" aria-label="ONVI WORKS — Domov">
          {rail ? (
            <img src={BRAND_MARK} alt="" className="h-7 w-7 rounded-full object-contain" />
          ) : (
            <img src={BRAND_LOGO} alt="ONVI WORKS" className="h-[20px] w-auto shrink-0" />
          )}
        </Link>
        <div className={clsx('flex items-center', rail ? 'mt-1 flex-col' : 'ml-auto gap-0.5')}>
          {bell}
          <button
            type="button"
            className="sidebar-tool"
            onClick={onToggleRail}
            aria-label={rail ? 'Rozbaliť bočný panel' : 'Zúžiť bočný panel'}
            aria-pressed={rail}
            title={rail ? 'Rozbaliť panel ([)' : 'Zúžiť panel ([)'}
          >
            {rail ? (
              <ChevronDoubleRightIcon className="h-4 w-4" aria-hidden="true" />
            ) : (
              <ChevronDoubleLeftIcon className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      <div className={clsx('pb-2', rail ? 'px-2' : 'px-3')}>
        <button
          type="button"
          className={clsx('sidebar-search', rail && 'justify-center')}
          onClick={onOpenSearch}
          aria-label="Hľadať"
          title="Hľadať (⌘K alebo /)"
        >
          <MagnifyingGlassIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          {rail ? null : (
            <>
              <span className="flex-1 text-left">Hľadať</span>
              <kbd className="sidebar-kbd">⌘K</kbd>
            </>
          )}
        </button>
      </div>

      <nav className={clsx('no-scrollbar flex-1 space-y-0.5 overflow-y-auto pb-3', rail ? 'px-2' : 'px-3')}>
        <p className="sidebar-heading">{rail ? <span className="sr-only">Dnes</span> : 'Dnes'}</p>
        {navigation.today.map((item) => (
          <NavRow key={item.to} item={item} count={countFor(item)} rail={rail} />
        ))}

        {navigation.team.length > 0 ? (
          <>
            <p className="sidebar-heading">{rail ? <span className="sr-only">Tím</span> : 'Tím'}</p>
            {navigation.team.map((item) => (
              <NavRow key={item.to} item={item} count={countFor(item)} rail={rail} />
            ))}
          </>
        ) : null}

        <div className={clsx('sidebar-heading flex items-center gap-2', rail && 'justify-center')}>
          {/* Nadpis skupiny je zároveň cesta na zoznam všetkých projektov. */}
          <NavLink
            to="/projects"
            end
            className={({ isActive }) => clsx('sidebar-heading-link', isActive && 'is-active')}
            title={rail ? 'Všetky projekty' : undefined}
          >
            {rail ? (
              <FolderIcon className="h-[18px] w-[18px]" aria-label="Všetky projekty" />
            ) : (
              <>
                Projekty
                {projects.length > 0 ? (
                  <span className="ml-1.5 font-normal normal-case tabular-nums opacity-80">{projects.length}</span>
                ) : null}
              </>
            )}
          </NavLink>
          {canCreateProjects && !rail ? (
            // „+“ je v nadpise, aby ho zoznam, ku ktorému patrí, nikdy nevytlačil dole.
            <NavLink to="/projects" aria-label="Nový projekt" title="Nový projekt" className="sidebar-tool ml-auto -my-1 h-6 w-6">
              <PlusIcon className="h-4 w-4" aria-hidden="true" />
            </NavLink>
          ) : null}
        </div>

        {projects.length === 0 ? (
          rail ? null : <p className="px-3 py-1 text-[13px]" style={{ color: 'var(--ww-sidebar-muted)' }}>Žiadne projekty</p>
        ) : (
          shown.map((project) => {
            const live = project.workers.length > 0;
            const mine = myUserId !== undefined && project.workers.some((w) => w.userId === myUserId);
            return (
              <NavLink
                key={project.id}
                to={`/projects/${project.id}`}
                className={itemClass}
                title={rail ? project.name : undefined}
              >
                <span
                  className={clsx('project-dot', rail && 'project-dot-rail')}
                  style={{ backgroundColor: project.color }}
                  aria-hidden="true"
                >
                  {rail ? project.name.trim().charAt(0).toUpperCase() : null}
                </span>
                <span className="sidebar-label truncate">{project.name}</span>
                {live ? (
                  <span
                    className={clsx('project-live', mine && 'project-live-mine', !rail && 'ml-auto')}
                    aria-label={mine ? 'beží tvoja práca' : 'práve sa na ňom pracuje'}
                    title={mine ? 'Beží tvoja práca' : 'Práve sa na ňom pracuje'}
                  />
                ) : null}
                {project.unreadMessages > 0 ? (
                  <span
                    className={clsx('sidebar-badge', live && !rail && 'ml-1.5')}
                    aria-label={`${project.unreadMessages} neprečítaných správ`}
                  >
                    {formatBadge(project.unreadMessages)}
                  </span>
                ) : null}
              </NavLink>
            );
          })
        )}

        {!rail && projects.length > SIDEBAR_PROJECTS_VISIBLE ? (
          <button
            type="button"
            onClick={() => setAllProjectsShown((value) => !value)}
            className="sidebar-more"
          >
            {allProjectsShown ? 'Zobraziť menej' : `Zobraziť všetkých ${projects.length}`}
          </button>
        ) : null}
      </nav>

      <div className={clsx('space-y-0.5 border-t py-2', rail ? 'px-2' : 'px-3')} style={{ borderColor: 'var(--ww-sidebar-border)' }}>
        {navigation.jarvis ? (
        <button
          type="button"
          className="sidebar-item w-full"
          onClick={onOpenJarvis}
          aria-expanded={jarvisOpen}
          title={rail ? 'JARVIS (⌘J)' : undefined}
        >
          <img src={BRAND_MARK} alt="" aria-hidden="true" className="sidebar-icon rounded-full object-contain" />
          <span className="sidebar-label">JARVIS</span>
          {rail ? null : (
            <>
              <span className="beta-chip">beta</span>
              <kbd className="sidebar-kbd ml-auto">⌘J</kbd>
            </>
          )}
        </button>
        ) : null}
        <NavLink to="/settings" className={itemClass} title={rail ? 'Nastavenia' : undefined}>
          <Cog6ToothIcon className="sidebar-icon" aria-hidden="true" />
          <span className="sidebar-label">Nastavenia</span>
          {hasAppUpdate ? (
            <span
              className={clsx('h-2 w-2 rounded-full bg-red-500', rail ? 'absolute right-2 top-1.5' : 'ml-auto')}
              aria-label="Aktualizácia aplikácie je k dispozícii"
            />
          ) : null}
        </NavLink>
        {profile}
      </div>
    </aside>
  );
};
